import React, { useEffect, useState, useCallback } from "react";
import styled from "styled-components";
import { useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { Autocomplete } from "@mui/material";

import TextField from "src/components/core/TextField";
import device from "src/styles/device";

const WrapperAutocomplete = styled.div`
  width: 350px;
  margin: 16px;

  @media ${device.mobileL} {
    width: 250px;
    margin: 8px 8px;
  }
`;

// This uses the Combobox from "react-widgets" (https://jquense.github.io/react-widgets/docs/Combobox)
export const AutocompleteCustomHybrid = ({
  placeholder = "Search for a place",
  onPlaceSelect,
}) => {
  const map = useMap();
  const places = useMapsLibrary("places");

  // https://developers.google.com/maps/documentation/javascript/reference/places-autocomplete-service#AutocompleteSessionToken
  const [sessionToken, setSessionToken] = useState(null);

  // https://developers.google.com/maps/documentation/javascript/reference/places-autocomplete-service
  const [autocompleteService, setAutocompleteService] = useState(null);

  // https://developers.google.com/maps/documentation/javascript/reference/places-service
  const [placesService, setPlacesService] = useState(null);

  // const [predictionResults, setPredictionResults] = useState<
  //   Array<google.maps.places.AutocompletePrediction>
  // >([]);
  const [predictionResults, setPredictionResults] = useState([]);

  const [inputValue, setInputValue] = useState("");

  const [fetchingData, setFetchingData] = useState(false);

  useEffect(() => {
    if (!places || !map) return;

    setAutocompleteService(new places.AutocompleteService());
    setPlacesService(new places.PlacesService(map));
    setSessionToken(new places.AutocompleteSessionToken());

    return () => setAutocompleteService(null);
  }, [map, places]);

  const fetchPredictions = useCallback(
    async (inputValue) => {
      if (!autocompleteService || !inputValue) {
        return;
      }

      setFetchingData(true);

      const request = { input: inputValue, sessionToken };
      const response = await autocompleteService.getPlacePredictions(request);

      setPredictionResults(response.predictions);
      setFetchingData(false);
    },
    [autocompleteService, sessionToken],
  );

  const onInputChange = useCallback(
    (_, value, reason) => {
      if (reason === "input" && typeof value === "string") {
        setInputValue(value);
        fetchPredictions(value);
      }
    },
    [fetchPredictions],
  );

  const onSelect = useCallback(
    // (prediction: google.maps.places.AutocompletePrediction | string) => {
    (_, prediction) => {
      if (!places || typeof prediction === "string") return;

      setFetchingData(true);

      if (!!prediction) {
        const detailRequestOptions = {
          placeId: prediction?.place_id,
          fields: ["geometry", "name", "formatted_address"],
          sessionToken,
        };

        const detailsRequestCallback = (
          // placeDetails: google.maps.places.PlaceResult | null
          placeDetails,
        ) => {
          // console.log('==== placeDetails ===', placeDetails);
          onPlaceSelect(placeDetails);
          setInputValue(placeDetails?.formatted_address ?? "");
          setSessionToken(new places.AutocompleteSessionToken());

          setFetchingData(false);
        };

        placesService?.getDetails(detailRequestOptions, detailsRequestCallback);
      } else {
        setInputValue("");
        onPlaceSelect(null);
      }
    },
    [onPlaceSelect, places, placesService, sessionToken],
  );

  return (
    <WrapperAutocomplete>
      <Autocomplete
        aria-required={true}
        id="search-maps"
        // fullWidth
        loading={fetchingData}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          return (
            <li key={key} {...optionProps}>
              {option?.description || ""}
            </li>
          );
        }}
        options={predictionResults}
        inputValue={inputValue}
        onInputChange={onInputChange}
        onChange={onSelect}
        getOptionLabel={(option) => option?.description || ""}
        sx={{
          ".MuiOutlinedInput-root": {
            background: "white",
          },
          "&.MuiAutocomplete-root .MuiOutlinedInput-root": {
            padding: "4px 9px",
          },
        }}
        renderInput={(params) => (
          <TextField {...params} fullWidth placeholder={placeholder} />
        )}
      />
    </WrapperAutocomplete>
  );
};
